import React, { useState, useEffect } from 'react'
import { UserPlus, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Select,
  SelectOption,
} from '../ui'
import { InvitationCodeDisplay } from './InvitationCodeDisplay'
import { useRoles } from '../../hooks/useRoles'
import { apiClient } from '../../lib/api'
import type { Employee } from '../../hooks/useEmployees'

interface CreateInvitationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  employee: Employee | null
}

interface InvitationResult {
  id: string
  invite_code: string
  expires_at: string
  is_valid: boolean
}

export function CreateInvitationDialog({
  open,
  onOpenChange,
  employee,
}: CreateInvitationDialogProps) {
  const [selectedRoleId, setSelectedRoleId] = useState<string>('')
  const [isCreating, setIsCreating] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [createdInvitation, setCreatedInvitation] = useState<InvitationResult | null>(null)
  const [existingInvitation, setExistingInvitation] = useState<InvitationResult | null>(null)

  const { data: rolesData, isLoading: rolesLoading } = useRoles({ limit: 100 })

  // Load employee data with invitation when dialog opens
  useEffect(() => {
    const loadEmployeeData = async () => {
      if (!open || !employee) {
        setExistingInvitation(null)
        setCreatedInvitation(null)
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const response = await apiClient.getEmployee(employee.id)
        const employeeData = response.data || response

        // Check if employee already has a user account
        if (employeeData.user_id) {
          setError('У сотрудника уже есть зарегистрированная учётная запись. Создание нового кода приглашения невозможно.')
          setExistingInvitation(null)
          setCreatedInvitation(null)
          return
        }

        // Check if employee has an active invitation
        if (employeeData.invitation && employeeData.invitation.is_valid) {
          setExistingInvitation(employeeData.invitation)
          setCreatedInvitation(employeeData.invitation)
        } else {
          setExistingInvitation(null)
          setCreatedInvitation(null)
        }
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(`Не удалось загрузить данные сотрудника: ${err.message}`)
        } else {
          setError('Не удалось загрузить данные сотрудника')
        }
      } finally {
        setIsLoading(false)
      }
    }

    loadEmployeeData()
  }, [open, employee])

  const handleClose = () => {
    setSelectedRoleId('')
    setError(null)
    setCreatedInvitation(null)
    setExistingInvitation(null)
    onOpenChange(false)
  }

  const handleCreateInvitation = async () => {
    if (!employee || !selectedRoleId) {
      setError('Пожалуйста, выберите роль')
      return
    }

    setIsCreating(true)
    setError(null)

    try {
      const response = await apiClient.createInvitationForEmployee(employee.id, selectedRoleId)
      setCreatedInvitation(response.invitation)
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message)
      } else if (typeof err === 'object' && err !== null && 'message' in err) {
        setError(String(err.message))
      } else {
        setError('Не удалось создать код приглашения')
      }
      // console.error('Failed to create invitation:', err)
    } finally {
      setIsCreating(false)
    }
  }

  const getEmployeeFullName = (emp: Employee) => {
    const parts = [emp.last_name, emp.first_name, emp.middle_name].filter(Boolean)
    return parts.join(' ')
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Создать код приглашения
            </div>
          </DialogTitle>
          <DialogDescription>
            {employee && (
              <span>
                Создание кода приглашения для сотрудника:{' '}
                <span className="font-medium">{getEmployeeFullName(employee)}</span>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            <span className="ml-3 text-gray-600">Загрузка данных...</span>
          </div>
        ) : !createdInvitation ? (
          <>
            <div className="space-y-4 py-4">
              {/* Employee info */}
              {employee && (
                <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                  <p className="text-sm text-gray-600">Сотрудник</p>
                  <p className="font-medium">{getEmployeeFullName(employee)}</p>
                  <p className="text-sm text-gray-500">
                    Табельный номер: {employee.employee_number}
                  </p>
                  {employee.iin && (
                    <p className="text-sm text-gray-500">ИИН: {employee.iin}</p>
                  )}
                </div>
              )}

              {/* Role selection */}
              <div className="space-y-2">
                <label htmlFor="role" className="text-sm font-medium text-gray-700">
                  Роль в системе <span className="text-red-500">*</span>
                </label>
                <Select
                  id="role"
                  value={selectedRoleId}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setSelectedRoleId(e.target.value)
                  }
                  disabled={rolesLoading || isCreating}
                >
                  <SelectOption value="">Выберите роль</SelectOption>
                  {rolesData?.data?.map((role) => (
                    <SelectOption key={role.id} value={role.id}>
                      {role.name}
                    </SelectOption>
                  ))}
                </Select>
                <p className="text-xs text-gray-500">
                  Эта роль определит уровень доступа сотрудника в системе
                </p>
              </div>

              {/* Error message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {/* Info message */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Важно:</strong> Код приглашения будет действителен 7 дней. Сотрудник
                  должен использовать его для регистрации в мобильном приложении с корректным ИИН.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={isCreating}>
                Отмена
              </Button>
              <Button onClick={handleCreateInvitation} disabled={!selectedRoleId || isCreating}>
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Создание...
                  </>
                ) : (
                  'Создать код'
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-4 py-4">
              {/* Success message */}
              <div
                className={`border rounded-lg p-3 ${
                  existingInvitation
                    ? 'bg-blue-50 border-blue-200'
                    : 'bg-green-50 border-green-200'
                }`}
              >
                <p
                  className={`text-sm font-medium ${
                    existingInvitation ? 'text-blue-800' : 'text-green-800'
                  }`}
                >
                  {existingInvitation
                    ? 'ℹ️ У сотрудника уже есть активный код приглашения'
                    : '✓ Код приглашения успешно создан!'}
                </p>
              </div>

              {/* Display invitation code */}
              <InvitationCodeDisplay
                inviteCode={createdInvitation.invite_code}
                expiresAt={createdInvitation.expires_at}
                isValid={createdInvitation.is_valid}
              />
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>Закрыть</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
